import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import { SendTo, Rater } from './models';
import * as R from 'ramda';
import { Map } from '../../../language/models';


const KEY_ACTION = ActionType.SendForApprovalRequest;
const ACTIONS    = [
  ActionType.SendForApprovalRequest,
  ActionType.SendForApprovalResponse,
  ActionType.SendForApprovalError,
];


type Payload = string;


type Meta = {
  projectId: number;
};


/**
 * Dispatch this action to request approval for the participant's added raters from the designated approver
 *
 * @param projectId - the project to which we wish to invite raters
 * @param participantId - the specific project participant for whom we wish to invite raters
 * @param comment - a custom comment included in the email sent out to invited raters
 * @param to - the list of rater email addresses to invite (along with the database Id for the language to be used for
 * each message)
 */
export const sendForApprovalAction = (
  projectId: number,
  participantId: number,
  comment: string,
  to: SendTo[],
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SendForApproval,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({ projectId: projectId, participantId: participantId, comment: comment, to: to }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { projectId: projectId }),
    bailout:  state => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const allRaters = state.projectRaters?.[action.meta.projectId] || {};
  const approvableIds = R.filter(
    key => allRaters[key].approvalStatus === 0 || allRaters[key].approvalStatus === 2,
    Object.keys(allRaters),
  );
  const approvableRaters = R.pick(approvableIds, allRaters) as Map<Rater>;
  const setToWaiting = Object.values(approvableRaters).reduce(
    (map, rater) => R.assoc(`${rater.raterId}`, R.mergeRight(rater, { approvalStatus: 5 }), map),
    {},
  );

  const merged = R.mergeRight(allRaters, setToWaiting);
  return R.assocPath(
    ['projectRaters', `${action.meta.projectId}`],
    merged,
    successState,
  );  
};

export const sendForApprovalReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
